import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Loader2, SlidersHorizontal, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth-context";
import { Button } from "../components/ui/button";
import useProjectData from "../hooks/useProjectData";
import { ProjectStepper } from "../components/project/ProjectStepper";
import { ProjectStatusLine, ProjectWarnings } from "../components/project/ProjectStatusLine";
import { ProjectActionsMenu } from "../components/project/ProjectActionsMenu";
import { VideoSettingsDrawer } from "../components/project/VideoSettingsDrawer";
import { CostConfirm } from "../components/project/CostConfirm";
import { displayMoney } from "../utils/pricing";
import { AiFixPanel } from "../components/project/AiFixPanel";
import StoryboardEditor from "./StoryboardEditor";
import { VideoStudioDetail } from "./VideoStudio";

function DocumentStep({ p }) {
    return (
        <div data-testid="project-document-step" className="rounded-lg border border-neutral-200 bg-white p-6">
            <div className="text-[11px] font-medium uppercase tracking-widest text-neutral-500">Source document</div>
            <div className="mt-2 text-lg font-medium text-neutral-900">{p.documentTitle || "—"}</div>
            <p className="mt-1 text-sm text-neutral-500">
                {p.versionId ? "The script and scenes were written from this document." : "Generate the script to turn this document into scenes."}
            </p>
            {p.documentId && (
                <Link to={`/knowledge/documents/${p.documentId}`} data-testid="project-open-document" className="mt-4 inline-block text-sm font-medium text-neutral-900 underline">
                    Open document
                </Link>
            )}
        </div>
    );
}

function primaryAction(p, act) {
    if (p.running) return { label: "Building…", disabled: true, key: "building" };
    if (!p.versionId) return { label: "Generate script", key: "generate", run: act.generate };
    if (p.needsReview > 0) {
        return { label: p.approved === 0 ? "Approve all" : `Approve remaining ${p.needsReview}`, key: "approve", run: act.approveAll };
    }
    if (!p.latestIsCurrent) return { label: "Make video", key: "render", run: act.render };
    if (!p.published) return { label: "Publish", key: "publish", run: act.publish };
    return { label: "Published", disabled: true, key: "published" };
}

export default function ProjectPage() {
    const { id } = useParams();
    const nav = useNavigate();
    const [sp, setSp] = useSearchParams();
    const { user } = useAuth();
    const { project: p, reload } = useProjectData(id);
    const [confirm, setConfirm] = useState(null);
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [mountKey, setMountKey] = useState(0);
    const [againCost, setAgainCost] = useState(null);
    const [aiOpen, setAiOpen] = useState(false);
    const showAgain = !!p?.latestIsCurrent && !p?.running;
    useEffect(() => {
        if (!showAgain) return;
        api.get(`/modules/${id}/cost-estimate?action=render`).then((r) => setAgainCost(r.data.raw_usd)).catch(() => setAgainCost(null));
    }, [id, showAgain]);

    if (!p) return <div className="p-10 text-sm text-neutral-500" data-testid="project-loading">Loading…</div>;

    const step = sp.get("step") || (!p.versionId ? "document" : p.storyboardApproved ? "video" : "script");
    const setStep = (s) => { const n = new URLSearchParams(sp); n.set("step", s); n.delete("scene"); setSp(n, { replace: true }); };
    const refresh = async () => { await reload(); setMountKey((k) => k + 1); };
    const guarded = (fn) => async () => {
        setBusy(true);
        try { await fn(); await refresh(); } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
    };
    const isAdmin = user?.role === "ORG_ADMIN" || user?.role === "LD_ADMIN";

    const doRender = guarded(async () => {
        if (!p.preflightOk) throw new Error("A few scenes still need finishing — fix the problems listed first.");
        await api.post(`/modules/${id}/render`, { trigger: "user_click" });
        toast.success("Video build started", { duration: 8000 });
        setStep("video");
    });
    const act = {
        generate: () => setConfirm({
            title: "Generate the script?", okLabel: "Generate script",
            estimate: { moduleId: id, action: "generate_script" },
            description: "Writes the script and scenes from the document.",
            onConfirm: guarded(async () => {
                await api.post(`/modules/${id}/generate`);
                toast.success("Writing the script…", { duration: 8000 });
                setStep("script");
            }),
        }),
        approveAll: () => setConfirm({
            title: p.approved === 0 ? "Approve all scenes?" : `Approve the remaining ${p.needsReview} scenes?`,
            okLabel: "Approve", free: true,
            description: "Approving doesn't build the video. You choose when to make it.",
            onConfirm: guarded(async () => {
                const { data } = await api.post(`/modules/${id}/scenes/approve-all`, {});
                toast.success(`${data.approved_now} scene${data.approved_now === 1 ? "" : "s"} approved · ${data.scenes_approved} of ${data.scenes_total} approved`, { duration: 8000 });
                if (data.failed?.length) toast.error(`${data.failed.length} scene(s) couldn't be approved`);
            }),
        }),
        render: () => setConfirm({
            title: p.latest ? "Make the video again?" : "Make the video?", okLabel: "Make video",
            estimate: { moduleId: id, action: "render" },
            description: "Builds the full video from the approved scenes. Reused clips cost nothing.",
            onConfirm: doRender,
        }),
        publish: guarded(async () => {
            await api.post(`/modules/${id}/publish`);
            toast.success("Published", { duration: 8000 });
        }),
    };
    const primary = primaryAction(p, act);

    const menu = [
        { key: "settings", label: "Video settings", onSelect: () => setSettingsOpen(true) },
        { key: "fix", label: "Fix problems", hidden: !isAdmin, onSelect: guarded(async () => { await api.post(`/modules/${id}/auto-fix`); toast.success("Fixing problems…", { duration: 8000 }); }) },
        { key: "recheck", label: "Re-check", onSelect: () => refresh() },
        { key: "render-again", label: `Make video again${againCost != null ? ` · ${againCost === 0 ? "Free" : `$${displayMoney(againCost)}`}` : ""}`, hidden: !p.latestIsCurrent || p.running, onSelect: act.render },
        { key: "cancel", label: "Cancel video build", hidden: !p.running, onSelect: guarded(async () => { await api.post(`/render-jobs/${p.currentJob.id}/cancel`); toast.success("Cancelled"); }) },
        { key: "content-check", label: "Content check", hidden: !p.versionId, separatorBefore: true, onSelect: () => nav(`/content/storyboards/${id}/fidelity`) },
        {
            key: "unstick", label: "Unstick video", hidden: user?.role !== "ORG_ADMIN",
            onSelect: guarded(async () => {
                if (!window.confirm("Cancel any running build and unstick this video?")) return;
                const { data } = await api.post(`/modules/${id}/reset-render-state`);
                toast.success(`Video unstuck (${data.jobs_failed} build(s) cleared)`, { duration: 8000 });
            }),
        },
    ];

    return (
        <div data-testid="project-page" className="space-y-5">
            <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="space-y-3">
                    <Link to="/videos" data-testid="project-back" className="inline-flex items-center gap-1 text-xs text-neutral-500 hover:text-neutral-900">
                        <ArrowLeft size={12} /> Videos
                    </Link>
                    <h1 data-testid="project-title" className="text-2xl font-medium tracking-tight text-neutral-900">{p.module.name}</h1>
                    <ProjectStepper step={step} doneUpTo={!p.versionId ? "document" : p.needsReview > 0 ? "script" : "video"} onChange={setStep} />
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => setSettingsOpen(true)} data-testid="open-video-settings">
                        <SlidersHorizontal size={14} className="mr-1.5" /> Video settings
                    </Button>
                    <Button
                        data-testid={`primary-action-${primary.key}`}
                        disabled={primary.disabled || busy}
                        onClick={primary.run}
                        className="bg-neutral-900 text-white hover:bg-neutral-800"
                    >
                        {busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}{primary.label}
                    </Button>
                    <ProjectActionsMenu items={menu} />
                </div>
            </div>
            <ProjectStatusLine p={p} />
            <ProjectWarnings p={p} moduleId={id} onFixed={reload} />
            <div data-testid={`project-step-${step}`}>
                {step === "document" && <DocumentStep p={p} />}
                {step === "script" && <StoryboardEditor key={`s${mountKey}`} embedded onChanged={reload} />}
                {step === "video" && <VideoStudioDetail key={`v${mountKey}`} embedded currentDurationS={p.durationS} />}
            </div>
            <VideoSettingsDrawer open={settingsOpen} onOpenChange={setSettingsOpen} module={p.module} onSaved={reload} />
            <CostConfirm confirm={confirm} onClose={() => setConfirm(null)} />
            {step === "video" && p.latest && (
                <button type="button" data-testid="ai-fix-fab" onClick={() => setAiOpen(true)}
                    className="fixed bottom-6 right-6 z-40 flex items-center gap-2 rounded-full bg-neutral-900 px-5 py-3 text-sm font-medium text-white shadow-lg transition-transform hover:scale-105">
                    <Sparkles size={16} /> Fix with AI
                </button>
            )}
            <AiFixPanel open={aiOpen} onOpenChange={setAiOpen} moduleId={id} onApplied={refresh}
                getTimestamp={() => { const v = document.querySelector("[data-testid='current-render-video']"); return v && v.currentTime > 0 ? v.currentTime : null; }} />
        </div>
    );
}
